import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";

// Reassigns a staff member to a different role - department is only picked
// here to narrow the role dropdown (Staff itself never stores departmentId,
// it's always derived by joining through roleId), so only roleId is ever
// sent to the API.
const StaffRoleModal = ({ staffId, currentDepartmentId, currentRoleId, departments = [], onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const [departmentId, setDepartmentId] = useState(currentDepartmentId || "");
    const [roleId, setRoleId] = useState(currentRoleId || "");
    const [roleOptions, setRoleOptions] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!departmentId) {
            setRoleOptions([]);
            setLoadingRoles(false);
            return;
        }
        (async () => {
            setLoadingRoles(true);
            const response = await Handler({ method: "GET", url: `/admin/staff/department/${departmentId}/roles` });
            setRoleOptions(response?.success ? response?.data?.data || [] : []);
            setLoadingRoles(false);
        })();
    }, [departmentId]);

    const onDepartmentChange = (value) => {
        setDepartmentId(value);
        if (value !== currentDepartmentId) setRoleId("");
    };

    const onSubmit = async () => {
        if (!roleId) {
            toast.error("Select a role");
            return;
        }
        if (roleId === currentRoleId) {
            toast.info("That's already this staff member's role");
            return;
        }
        setSaving(true);
        const response = await Handler({ method: "patch", url: `/admin/staff/${staffId}/role`, data: { roleId } });
        if (response?.success) {
            toast.success(response?.data?.message || "Role reassigned");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to reassign role"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">Reassign Role</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label">Department</label>
                                <select className="form-select" value={departmentId} onChange={(e) => onDepartmentChange(e.target.value)}>
                                    <option value="" disabled>Select department</option>
                                    {departments.map((d) => (<option key={d.departmentId} value={d.departmentId}>{d.departmentName}</option>))}
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label">Role</label>
                                <select className="form-select" value={roleId} onChange={(e) => setRoleId(e.target.value)} disabled={!departmentId || loadingRoles}>
                                    <option value="" disabled>{loadingRoles ? "Loading roles..." : "Select role"}</option>
                                    {roleOptions.map((r) => (<option key={r.roleId} value={r.roleId}>{r.roleName}</option>))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Saving..." : "Reassign"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StaffRoleModal;
